import {
  Modal,
  ModalContent,
  ModalOverlay,
  HStack,
  Button,
  Center,
  Spinner,
  Flex
} from '@chakra-ui/react';
import React, { FC } from 'react';

import { HeaderControls } from '@app/components/Swap/components/HeaderControls';
import { WithdrawLiquidity } from '@app/components/WithdrawLiquidity';
import { useWithdrawLiquidityAvailability } from '@app/hooks/liquidity/useWithdrawLiquidityAvailability';
import { AddLiquidity } from '@app/screens/liquidity/AddLiquidity';
import { Pair, PairToken } from '@app/types/pool';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { useIsHiddenPool } from '@app/hooks/pools/useIsHiddenPool';
import { useConfig } from '@app/config';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  data: {
    type: 'add' | 'withdraw';
    pair: Pair;
  } | null;
  onChangeView: (nextView: { type: 'add' | 'withdraw'; pair: Pair }) => void;
}

export const PositionActionsModal: FC<Props> = ({
  onClose,
  isOpen,
  data,
  onChangeView
}) => {
  const {
    isAllowedToWithdraw,
    balance,
    getWithdrawBalance,
    loading: loadingWithdrawData
  } = useWithdrawLiquidityAvailability(data?.pair.id);
  const isHiddenPool = useIsHiddenPool(data?.pair.id);
  const config = useConfig();
  const TOKENS_WHITELIST = getTokensWhitelist(config);

  function getTokenData(
    dataFromGraph: PairToken | undefined,
    tokenAddress: string
  ) {
    if (dataFromGraph) {
      return {
        symbol: dataFromGraph.symbol,
        contractAddress: dataFromGraph.id,
        decimals: +dataFromGraph.decimals,
        name: dataFromGraph.name,
        logoUri: ''
      };
    }

    const wlData = TOKENS_WHITELIST.find(
      item => item.contractAddress === tokenAddress
    );

    if (wlData) {
      return {
        symbol: wlData.symbol,
        contractAddress: wlData.contractAddress,
        decimals: +wlData.decimals,
        name: wlData.name,
        logoUri: ''
      };
    }
  }

  function renderContent() {
    if (!config) {
      return null;
    }

    if (data?.type === 'add') {
      const token0Data =
        data.pair.token0.id.toLowerCase() ===
        config?.CONTRACTS.WETH.toLowerCase()
          ? getTokenData(undefined, NATIVE_TOKEN_ADDRESS)
          : getTokenData(data.pair.token0, data.pair.token0.id);
      const token1Data =
        data.pair.token1.id.toLowerCase() ===
        config?.CONTRACTS.WETH.toLowerCase()
          ? getTokenData(undefined, NATIVE_TOKEN_ADDRESS)
          : getTokenData(data.pair.token1, data.pair.token1.id);

      if (!token0Data || !token1Data) {
        return null;
      }

      return (
        <AddLiquidity
          inModalView
          initialData={{
            from: {
              amount: '',
              token: token0Data
            },
            to: {
              amount: '',
              token: token1Data
            }
          }}
        />
      );
    }

    if (loadingWithdrawData) {
      return (
        <Flex w="100%" h="100%" minHeight="300px" justifyContent="center">
          <Center>
            <Spinner color="orange" width={8} height={8} />
          </Center>
        </Flex>
      );
    }

    if (!isAllowedToWithdraw || balance === undefined || !data?.pair) {
      return null;
    }

    return (
      <WithdrawLiquidity
        pair={data?.pair}
        balance={balance}
        getWithdrawBalance={getWithdrawBalance}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <HStack mb="20px">
          <HStack
            w="100%"
            alignItems="flex-start"
            gap="3px"
            px="8px"
            py="8px"
            sx={{ background: 'neutral.1000' }}
          >
            <Button
              isDisabled={isHiddenPool}
              variant="tertiary"
              onClick={() => {
                if (!data) {
                  return;
                }

                onChangeView({
                  type: 'add',
                  pair: data.pair
                });
              }}
              sx={{
                color: data?.type === 'add' ? 'orange' : 'gray.4',
                bg: data?.type === 'add' ? 'gray.2' : 'transparent'
              }}
            >
              Add
            </Button>
            <Button
              variant="tertiary"
              onClick={() => {
                if (!data) {
                  return;
                }

                onChangeView({
                  type: 'withdraw',
                  pair: data?.pair
                });
              }}
              sx={{
                color: data?.type === 'withdraw' ? 'orange' : 'gray.4',
                bg: data?.type === 'withdraw' ? 'gray.2' : 'transparent'
              }}
            >
              Withdraw
            </Button>
          </HStack>
          <HeaderControls />
        </HStack>
        {renderContent()}
      </ModalContent>
    </Modal>
  );
};
